import { useEffect, useId, type ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { drawerClosed, drawerToggled, selectDrawerOpen } from '../features/ui/uiSlice';

export interface DrawerProps {
  /** Accessible name of the panel, and its heading unless {@link heading} replaces it. */
  readonly title: string;
  /** Replaces the plain heading, e.g. with an editable title. */
  readonly heading?: ReactNode;
  /** Shown at the top of the panel, on the same row as (and left of) the handle. */
  readonly brand?: ReactNode;
  readonly children: ReactNode;
}

/**
 * Pull-out options panel on the right. A handle stays visible when it is closed; Escape closes
 * it. Closed content is inert so it cannot be focused or read out.
 */
export function Drawer({ title, heading, brand, children }: DrawerProps) {
  const open = useAppSelector(selectDrawerOpen);
  const dispatch = useAppDispatch();
  const panelId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(drawerClosed());
      }
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [open, dispatch]);

  return (
    <>
      <button
        type="button"
        className="drawer-handle contrast"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? 'Close options' : 'Open options'}
        onClick={() => dispatch(drawerToggled())}
      >
        {open ? '›' : '‹'}
      </button>
      <aside id={panelId} className="drawer" data-open={open} inert={!open} aria-label={title}>
        <div className="drawer-header">{brand}</div>
        {heading ?? <h2 className="drawer-title">{title}</h2>}
        {children}
      </aside>
    </>
  );
}
